import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import en from '../i18n/en.json';
import es from '../i18n/es.json';

const translations = { en, es };
const SUPPORTED_LOCALES = ['en', 'es'];
const DEFAULT_LOCALE = 'en';
const STORAGE_KEY = 'cii_language';

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
});

function resolve(obj, path) {
  return path.split('.').reduce(
    (acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined),
    obj
  );
}

function interpolate(str, params) {
  if (!params || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] !== undefined ? params[key] : `{${key}}`
  );
}

export default function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
  });

  const setLocale = useCallback((newLocale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setLocaleState(newLocale);
      localStorage.setItem(STORAGE_KEY, newLocale);
      document.documentElement.lang = newLocale;
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback((key, params) => {
    let value = resolve(translations[locale], key);
    if (value === undefined && locale !== DEFAULT_LOCALE) {
      value = resolve(translations[DEFAULT_LOCALE], key);
    }
    if (value === undefined) return key;
    return interpolate(value, params);
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() {
  const { t } = useContext(I18nContext);
  return t;
}

export function useI18n() {
  return useContext(I18nContext);
}

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
